// Purpose: sender-side share API wrappers. Reads the CSRF cookie on each
// state-changing call and echoes it via X-CSRF-Token.

import { api } from './client.ts';

export type ShareStatus = 'DRAFT' | 'LIVE' | 'REVOKED' | 'EXPIRED' | 'CONSUMED';

export interface ShareSummary {
  id: string;
  name: string | null;
  status: ShareStatus;
  createdAt: string;
  expiresAt: string | null;
  consumedAt: string | null;
  consumedViaZip: boolean;
  sizeBytes: string;
  fileCount: number;
  locked: boolean;
  // Commit 8b.16: owner-driven UI organization. Orthogonal to status.
  isArchived: boolean;
  // 8b.16: per-share recipient summary, computed via aggregate join in
  // ShareService.recipientSummaryByShare. Pre-8b.14 unbackfillable
  // shares show 0/0/null (no ShareRecipient rows exist for them).
  recipientCount: number;
  accessedCount: number;
  lastActivityAt: string | null;
}

export type ShareListFilter = 'active' | 'archive' | 'all';

export interface ShareListResponse {
  shares: ShareSummary[];
  counts: { active: number; archive: number; all: number };
  filter: ShareListFilter;
}

export interface FileEntry {
  id: string;
  originalFilename: string;
  sizeBytes: string;
  mimeType: string;
  status: string;
  // Commit 8b.15: null for flat-root files; SPA detects any non-null
  // entry on the share to switch to tree view. Server returns this on
  // both owner detail and recipient files endpoints.
  relativePath: string | null;
}

export interface ShareRecipientView {
  id: string;
  email: string;
  status: 'PENDING' | 'NOTIFIED' | 'EMAIL_VERIFIED' | 'ACCESSED' | 'REVOKED' | 'EXPIRED';
  notifiedAt: string | null;
  emailVerifiedAt: string | null;
  lastAccessedAt: string | null;
  failedAttempts: number;
  lockedUntil: string | null;
  createdAt: string;
}

// Commit 8b.16: ShareDetail keeps lifecycle + isArchived but omits the
// list-only aggregate fields (recipientCount/accessedCount/lastActivityAt)
// — detail page renders the full per-recipient table from `recipients[]`,
// no derived summary needed.
export interface ShareDetail
  extends Omit<ShareSummary, 'fileCount' | 'recipientCount' | 'accessedCount' | 'lastActivityAt'> {
  accessCode: string | null;
  recipientHint: string | null;
  files: FileEntry[];
  // Commit 8b.14: present on ALL shares post-deploy (legacy backfilled to
  // length 1, new multi-recipient at finalize). Empty array only on
  // pre-8b.14 shares that couldn't be backfilled (no recipientEmail).
  recipients: ShareRecipientView[];
}

export interface CreatedRecipient {
  recipientId: string;
  email: string;
  link: string;
  accessCode: string;
}

// Commit 8b.14: new multi-recipient finalize response. Legacy 1-recipient
// callers still get the old shape ({linkUrl, accessCode}) — see
// FinalizeResponseLegacy.
export interface FinalizeResponseMulti {
  shareId: string;
  expiresAt: string;
  recipients: CreatedRecipient[];
}

export type FinalizeResponse = FinalizeResponseLegacy | FinalizeResponseMulti;

export interface FinalizeResponseLegacy {
  shareId: string;
  linkUrl: string;
  accessCode: string;
  expiresAt: string;
}

export function isLegacyFinalizeResponse(r: FinalizeResponse): r is FinalizeResponseLegacy {
  return 'linkUrl' in r;
}

export interface PresignResponse {
  fileId: string;
  uploadUrl: string;
  expiresAt: string;
  blobPath: string;
}

function readCookie(name: string): string | null {
  const match = document.cookie
    .split(';')
    .map((s) => s.trim())
    .find((s) => s.startsWith(name + '='));
  if (!match) return null;
  return decodeURIComponent(match.slice(name.length + 1));
}

function csrfHeaders(): Record<string, string> {
  const token = readCookie('ast_csrf');
  return token ? { 'X-CSRF-Token': token } : {};
}

// Commit 8b.16: returns shares + per-filter counts. Filter param defaults
// to 'active' if omitted; pre-8b.16 callers expecting the legacy "all"
// shape can pass filter:'all' explicitly.
export async function listShares(filter: ShareListFilter = 'active'): Promise<ShareListResponse> {
  const res = await api.get<ShareListResponse>('/shares', { params: { filter } });
  return res.data;
}

export async function archiveShare(id: string): Promise<void> {
  await api.post(`/shares/${id}/archive`, null, { headers: csrfHeaders() });
}
export async function unarchiveShare(id: string): Promise<void> {
  await api.post(`/shares/${id}/unarchive`, null, { headers: csrfHeaders() });
}
export async function bulkArchiveShares(
  shareIds: string[],
  archived: boolean
): Promise<{ flipped: number } | { offenders: BulkRevokeOffender[] }> {
  try {
    const res = await api.post<{ flipped: number }>(
      '/shares/bulk-archive',
      { shareIds, archived },
      { headers: csrfHeaders() }
    );
    return res.data;
  } catch (err) {
    const ax = err as {
      response?: { status?: number; data?: { offenders?: BulkRevokeOffender[] } };
    };
    if (ax.response?.status === 400 && Array.isArray(ax.response.data?.offenders)) {
      return { offenders: ax.response.data!.offenders! };
    }
    throw err;
  }
}

export async function getShare(id: string): Promise<ShareDetail> {
  const res = await api.get<ShareDetail>(`/shares/${id}`);
  return res.data;
}

export async function createDraft(): Promise<{ id: string }> {
  const res = await api.post<{ id: string }>('/shares/draft', null, {
    headers: csrfHeaders(),
  });
  return res.data;
}

export async function presignFile(
  draftId: string,
  input: {
    originalFilename: string;
    sizeBytes: number;
    mimeType: string;
    // Commit 8b.15: optional folder path. Pre-8b.15 callers omit it.
    relativePath?: string;
  }
): Promise<PresignResponse> {
  const res = await api.post<PresignResponse>(`/shares/${draftId}/files/presign`, input, {
    headers: csrfHeaders(),
  });
  return res.data;
}

export async function completeFile(
  draftId: string,
  fileId: string,
  rawDekBase64: string
): Promise<void> {
  await api.post(
    `/shares/${draftId}/files/${fileId}/complete`,
    { rawDek: rawDekBase64 },
    { headers: csrfHeaders() }
  );
}

// Commit 8b.13: report a browser-side blob PUT failure so the upload row
// transitions out of PENDING_UPLOAD and the audit chain captures the cause.
// Errors here are swallowed at the call site (uploadFile) — the user already
// has an upload error to deal with.
export async function reportFileError(
  draftId: string,
  fileId: string,
  errorReason: string
): Promise<void> {
  await api.post(
    `/shares/${draftId}/files/${fileId}/error`,
    { errorReason },
    { headers: csrfHeaders() }
  );
}

// Commit 8b.14: finalize accepts the new multi-recipient body shape.
// Legacy {recipientEmail} still works server-side for one cycle, but the
// SPA always sends the new shape now.
export async function finalize(
  draftId: string,
  input: {
    name: string;
    ttlHours: 1 | 2 | 8 | 24;
    recipients: { email: string }[];
    recipientHint?: string;
  }
): Promise<FinalizeResponseMulti> {
  const res = await api.post<FinalizeResponseMulti>(`/shares/${draftId}/finalize`, input, {
    headers: csrfHeaders(),
  });
  return res.data;
}

// Commit 8b.14: MFA-gated. Returns per-recipient link + access code so the
// owner can re-show / re-distribute. SPA wraps this in a step-up flow if
// the response is 428.
export async function recoverRecipients(id: string): Promise<{ recipients: CreatedRecipient[] }> {
  const res = await api.post<{ recipients: CreatedRecipient[] }>(
    `/shares/${id}/recover-recipients`,
    null,
    { headers: csrfHeaders() }
  );
  return res.data;
}

// Commit 8b.14: revoke a single recipient (MFA-gated, owner-or-admin).
export async function revokeRecipient(shareId: string, recipientId: string): Promise<void> {
  await api.post(`/shares/${shareId}/recipients/${recipientId}/revoke`, null, {
    headers: csrfHeaders(),
  });
}

// Commit 8b.14.1: per-recipient activity log (forensics timeline).
export interface RecipientActivityEntry {
  id: string;
  action: string;
  ipAddress: string | null;
  userAgent: string | null;
  occurredAt: string;
  metadata: unknown;
}
export async function getRecipientActivity(
  shareId: string,
  recipientId: string
): Promise<{ activity: RecipientActivityEntry[] }> {
  const res = await api.get<{ activity: RecipientActivityEntry[] }>(
    `/shares/${shareId}/recipients/${recipientId}/activity`
  );
  return res.data;
}

export async function revokeShare(id: string): Promise<void> {
  await api.post(`/shares/${id}/revoke`, null, { headers: csrfHeaders() });
}

// Commit 8b.1: owner link recovery. 428 means the caller needs a
// fresh MFA step-up; the server returns `stepUpUrl` in the body. The
// SPA navigates to that URL (full-page) and the /auth/step-up flow
// returns to the same detail page with a fresh mfaSatisfiedAt on the
// session, after which a retry succeeds. Caller is responsible for
// showing the returned publicUrl exactly once and not stashing it.
export async function recoverShareLink(id: string): Promise<{ publicUrl: string }> {
  const res = await api.post<{ publicUrl: string }>(`/shares/${id}/recover-link`, null, {
    headers: csrfHeaders(),
  });
  return res.data;
}

// Phase 7 share-extend. Ceiling is "now + AST_SHARE_MAX_TTL_HOURS"
// enforced server-side; a 400 here means either past-dated or past ceiling.
export async function extendShare(
  id: string,
  newExpiresAt: string
): Promise<{ oldExpiresAt: string; newExpiresAt: string }> {
  const res = await api.post<{ oldExpiresAt: string; newExpiresAt: string }>(
    `/shares/${id}/extend`,
    { newExpiresAt },
    { headers: csrfHeaders() }
  );
  return res.data;
}

export interface BulkRevokeOffender {
  id: string;
  reason: 'not_found' | 'already_revoked' | 'not_live';
}

// Phase 7 atomic bulk revoke. Returns the offender list when the server
// rejects the batch; callers highlight those rows and let the user retry.
export async function bulkRevokeShares(shareIds: string[]): Promise<
  | {
      ok: true;
    }
  | {
      ok: false;
      offenders: BulkRevokeOffender[];
    }
> {
  try {
    await api.post('/shares/bulk-revoke', { shareIds }, { headers: csrfHeaders() });
    return { ok: true };
  } catch (err) {
    // axios throws on non-2xx; inspect the shape the route returns.
    const anyErr = err as {
      response?: { status?: number; data?: { offenders?: BulkRevokeOffender[] } };
    };
    const status = anyErr.response?.status;
    const offenders = anyErr.response?.data?.offenders;
    if (status === 400 && Array.isArray(offenders)) {
      return { ok: false, offenders };
    }
    throw err;
  }
}
